import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common.js';

export const UserSchema = z.object({
  user_id: UuidSchema,
  azure_object_id: z.string().min(1).nullable().optional(),
  email: z.string().email(),
  name: z.string().min(1),
  job_title: z.string().nullable().optional(),
  m365_photo_url: z.string().url().nullable().optional(),
  is_admin: z.boolean(),
  active: z.boolean().optional(),
  deaktiviert_am: IsoDateTimeSchema.nullable().optional(),
  created_at: IsoDateTimeSchema,
  updated_at: IsoDateTimeSchema,
});
export type User = z.infer<typeof UserSchema>;

// ---------------------------------------------------------------------------
// V1.5 Persona + User Preferences (Phase 9)
// ---------------------------------------------------------------------------
// Persona-Schlüssel:
//   kl       — Kaufm. Leitung (Default für function_role kaufm_leitung + reader)
//   bl       — Bereichsleiter
//   manager  — abgeleitet (≥1 Tag mit manager_user_id=me)
//   pl       — Projektleiter
//   mtb      — MTB-Variante (KL-Sicht + responsibility=MTB Filter)
//   service  — Service-Variante (KL-Sicht + responsibility=Service Filter)

export const PersonaSchema = z.enum(['kl', 'bl', 'manager', 'pl', 'mtb', 'service']);
export type Persona = z.infer<typeof PersonaSchema>;

export const PERSONA_LABELS: Record<Persona, string> = {
  kl: 'Kaufm. Leitung',
  bl: 'Bereichsleitung',
  manager: 'Manager (Tag-Verantwortung)',
  pl: 'Projektleitung',
  mtb: 'MTB',
  service: 'Service-Leitung',
};

export const PhaseLensSchema = z.enum(['pipeline', 'kanban', 'matrix']);
export type PhaseLens = z.infer<typeof PhaseLensSchema>;

// ---------------------------------------------------------------------------
// Sticky Filters (Issue #63) — zuletzt gesetzte Filter je Sicht, geräteübergreifend
// ---------------------------------------------------------------------------
// Alle Felder jeder Sicht sind optional. Grund (gilt für alle vier Schemas):
// Teil-Snapshots aus älteren Clients und später ergänzte Filter dürfen ein
// gespeichertes Präferenz-Objekt nicht ungültig machen — sonst verlöre der Nutzer
// beim Deployment seine Einstellungen.
//
// In KEINEM Schema steht die Freitext-Suche: eine gemerkte Suche sähe beim
// nächsten Aufruf wie eine leere Liste aus (der Nutzer sucht nach der Ursache
// statt nach Projekten). Die Suche bleibt bewusst sitzungslokal.

// Phase-Board. `persona_default_active` merkt sich, ob die Persona-Standardsicht
// an/aus war; `lens` die zuletzt gewählte Linse (vormals localStorage `pv:phase-lens`).
export const BoardFiltersSchema = z.object({
  persona_default_active: z.boolean().optional(),
  segment: z.string().nullable().optional(),
  bereich: z.string().nullable().optional(),
  ms: z.string().nullable().optional(),
  mtb: z.string().nullable().optional(),
  pl: z.array(z.string()).optional(),
  /**
   * ALTBESTAND (vor #81): Einzel-Tag. Wird weiter GELESEN — als Migrationsquelle für
   * `tags` — und als Rollback-Brücke geschrieben, solange genau ein echter Tag gewählt
   * ist. **Nicht entfernen**, solange Bestandsnutzer nicht migriert sind: Sonst verliert
   * jeder, der vor der Umstellung einen Tag gefiltert hatte, seine Auswahl, und ein
   * Rollback des Deployments nähme sie ihm ein zweites Mal.
   */
  tag: z.string().nullable().optional(),
  /**
   * Mehrfachauswahl (#81). Enthält `tag_id`s und ggf. den Sentinel `__no_tags__`
   * (`NO_TAGS_VALUE`). Leeres Array = kein Filter.
   *
   * `.optional()` ist Pflicht: Ein Pflichtfeld würde jeden bereits gespeicherten
   * Snapshot ungültig machen — `parseStored` liefert dann `undefined`, und der Nutzer
   * verlöre ALLE Filter dieser Sicht, nicht nur den Tag.
   */
  tags: z.array(z.string()).optional(),
  manager: z.string().nullable().optional(),
  needs_only: z.boolean().optional(),
  blocked_only: z.boolean().optional(),
  overdue_only: z.boolean().optional(),
  /** Nur Projekte mit offenem Befund (PV-052). Wirkt nur bei Flag `befunde`. */
  befund_only: z.boolean().optional(),
  due_from: z.string().optional(),
  due_to: z.string().optional(),
  lens: PhaseLensSchema.optional(),
});
export type BoardFilters = z.infer<typeof BoardFiltersSchema>;

// Übergabe-Sicht.
//
// `mtb` unterscheidet DREI Zustände, und diese Unterscheidung ist der ganze Punkt:
//   fehlend (undefined) → nie gewählt; die Vorbelegung greift (Funktionsrolle `mtb`
//                         startet auf der eigenen Person) und bleibt lebendig.
//   null                → bewusst auf „alle" gestellt; gewinnt gegen die Vorbelegung.
//   string              → auf diese Person gestellt.
// Ohne den `undefined`-Fall wäre entweder die Vorbelegung tot (jeder Erstbesuch
// schriebe sie als Wahl fest) oder die Wahl „alle" ununterscheidbar von „nie
// gewählt" — dann käme die Vorbelegung bei jedem Aufruf zurück. Gleiche Regel wie
// bei `lens` oben, siehe `resolveStartValue`.
export const HandoverFiltersSchema = z.object({
  segment: z.string().optional(),
  mtb: z.string().nullable().optional(),
});
export type HandoverFilters = z.infer<typeof HandoverFiltersSchema>;

// #158: Statusfilter der Staffingseite — filtert auf den berechneten Positionsstatus
// (`status_anzeige`). „ausgeplant" ist kein Filterwert (zählt nicht mit). #163: `zu_bestaetigen`
// trifft Projekte mit ≥1 KI-Vorschlag.
export const StaffingFilterStatusSchema = z.enum([
  'offen',
  'vorgeschlagen',
  'float',
  'erledigt',
  'zu_bestaetigen',
]);
export type StaffingFilterStatus = z.infer<typeof StaffingFilterStatusSchema>;

// Staffing neu / KL-Board. `status` kennt zusätzlich 'all' (kein Statusfilter).
// `.catch(undefined)`: gemerkte Altwerte vor #158 (`open`/`staffed`/`planned`) fallen still weg,
// statt den ganzen Snapshot (Segment, PL, Tags) unbrauchbar zu machen.
export const StaffingFiltersSchema = z.object({
  segment: z.string().optional(),
  status: z
    .union([z.literal('all'), StaffingFilterStatusSchema])
    .optional()
    .catch(undefined),
  pl: z.array(z.string()).optional(),
  manager: z.string().nullable().optional(),
  /** ALTBESTAND (vor #81) — siehe die ausführliche Begründung an `BoardFiltersSchema.tag`. */
  tag: z.string().nullable().optional(),
  /** Mehrfachauswahl (#81), inkl. Sentinel `__no_tags__`. Siehe `BoardFiltersSchema.tags`. */
  tags: z.array(z.string()).optional(),
});
export type StaffingFilters = z.infer<typeof StaffingFiltersSchema>;

// Portfolio Atlas.
export const AtlasFiltersSchema = z.object({
  segment: z.string().nullable().optional(),
  bereich: z.string().nullable().optional(),
  ms: z.string().nullable().optional(),
  needs_only: z.boolean().optional(),
  missing_responsibility: z.boolean().optional(),
  my_projects_only: z.boolean().optional(),
  include_completed: z.boolean().optional(),
  overdue_only: z.boolean().optional(),
  due_from: z.string().optional(),
  due_to: z.string().optional(),
});
export type AtlasFilters = z.infer<typeof AtlasFiltersSchema>;

// KFML-Worklist: kein Filter, sondern die Tab-Wahl (vormals localStorage
// `pv:worklist-tab`). Liegt hier, weil sie derselben Mechanik folgt und der
// Sticky-Filters-Hook die einzige Stelle sein soll, die so etwas persistiert.
export const WorklistFiltersSchema = z.object({
  tab: z.enum(['staffing', 'worklist']).optional(),
});
export type WorklistFilters = z.infer<typeof WorklistFiltersSchema>;

// KFML-Worklist, Tab „Worklist" (#114): dieselben Filter wie „Staffing neu", ohne
// Status. Eigene Sicht statt `worklist`: unter `worklist` schreibt die Seite schon die
// Tab-Wahl, und ein Snapshot ersetzt immer die ganze Sicht — zwei Schreiber auf
// dieselbe Sicht würden sich gegenseitig die Werte löschen.
export const WorklistListFiltersSchema = z.object({
  segment: z.string().optional(),
  pl: z.array(z.string()).optional(),
  manager: z.string().nullable().optional(),
  /** Rollback-Brücke wie `StaffingFiltersSchema.tag` (#81). */
  tag: z.string().nullable().optional(),
  tags: z.array(z.string()).optional(),
});
export type WorklistListFilters = z.infer<typeof WorklistListFiltersSchema>;

// Projektdetail/Uebergabe: kein Filter, sondern der Auf-/Zuklapp-Zustand der
// Staffingbedarfs-Erfassungsmaske (PV-036). Analog zu WorklistFiltersSchema:
// liegt hier, weil der Sticky-Filters-Hook die einzige persistierende Stelle ist.
export const StaffingFormFiltersSchema = z.object({
  add_form_expanded: z.boolean().optional(),
});
export type StaffingFormFilters = z.infer<typeof StaffingFormFiltersSchema>;

// Sichtneutraler Container. Alle Sichten optional → ein Nutzer, der eine Sicht
// noch nie gefiltert hat, hat dort schlicht keinen Eintrag.
export const ViewFiltersSchema = z.object({
  board: BoardFiltersSchema.optional(),
  handover: HandoverFiltersSchema.optional(),
  staffing: StaffingFiltersSchema.optional(),
  atlas: AtlasFiltersSchema.optional(),
  worklist: WorklistFiltersSchema.optional(),
  worklist_list: WorklistListFiltersSchema.optional(),
  staffing_form: StaffingFormFiltersSchema.optional(),
});
export type ViewFilters = z.infer<typeof ViewFiltersSchema>;

// ---------------------------------------------------------------------------
// Gespeicherte Filter (#141) — persönlich, nur Phase Board
// ---------------------------------------------------------------------------
// Benannte Schnappschüsse der Board-Filter (inkl. Linse und Persona-Chip, ohne Freitextsuche).
// Bewusst NICHT in `view_filters.board`: dort ersetzt jeder Schnappschuss die ganze Sicht, ein
// gespeicherter Filter wäre nach dem nächsten Klick weg. In der Oberfläche heißen sie
// „Gespeicherter Filter" / „Meine Filter" — „Sicht" ist in der PV schon die Persona.
export const SAVED_BOARD_FILTERS_MAX = 20;
export const NAME_GESPEICHERTER_FILTER_MAX = 60;

export const SavedBoardFilterSchema = z.object({
  id: z.string().min(1),
  name: z.string().trim().min(1).max(NAME_GESPEICHERTER_FILTER_MAX),
  filters: BoardFiltersSchema,
  created_at: z.string(),
});
export type SavedBoardFilter = z.infer<typeof SavedBoardFilterSchema>;

export const SavedFiltersSchema = z.object({
  board: z
    .array(SavedBoardFilterSchema)
    .max(SAVED_BOARD_FILTERS_MAX)
    .refine(
      (liste) =>
        new Set(liste.map((f) => f.name.trim().toLocaleLowerCase('de'))).size === liste.length,
      { message: 'saved_filter_name_doppelt' },
    )
    .optional(),
});
export type SavedFilters = z.infer<typeof SavedFiltersSchema>;

export const UserPreferencesSchema = z.object({
  theme: z.enum(['mindsquare', 'mission-control']).optional(),
  locale: z.enum(['de', 'en']).optional(),
  density: z.enum(['calm', 'balanced', 'dense']).optional(),
  ai_prominence: z.enum(['home', 'copilot', 'feature']).optional(),
  default_phase_lens: PhaseLensSchema.optional(),
  active_persona: PersonaSchema.optional(),
  // Phase-Board: pro User ausgeblendete Phasen (F4). IDs der phases-Tabelle.
  hidden_phase_ids: z.array(z.string()).optional(),
  // ALTBESTAND (vor Issue #63): Phase-Board-Filter lagen direkt hier. Wird weiter
  // GELESEN (Migrationsquelle für view_filters.board), aber nicht mehr geschrieben.
  // Nicht entfernen, solange nicht sicher ist, dass alle Nutzer migriert sind —
  // sonst verlieren Bestandsnutzer beim Deployment ihre gesetzten Filter.
  board_filters: BoardFiltersSchema.optional(),
  // Sticky Filters je Sicht (Issue #63). Einzige Schreibstelle ist der
  // useStickyFilters-Hook im Web.
  view_filters: ViewFiltersSchema.optional(),
  // Gespeicherte Filter (#141). Einzige Schreibstelle: useSavedBoardFilters im Web; die API
  // ersetzt die Liste je Seite als Ganzes (mergePreferences).
  saved_filters: SavedFiltersSchema.optional(),
});
export type UserPreferences = z.infer<typeof UserPreferencesSchema>;

export const PersonasResponseSchema = z.object({
  available: z.array(PersonaSchema),
  active: PersonaSchema,
});
export type PersonasResponse = z.infer<typeof PersonasResponseSchema>;
